import {
  EmailLog,
  EmailStatus,
  EmailType,
} from '@tribeplatform/gql-client/types'
import { I18nShape } from '@tribeplatform/react-components/i18n'

import { EmailStatusRenderer } from './EmailStatusRenderer.js'

export const EMAIL_LOGS_PAGE_SIZE = 30

export const emailStatusMappingFactory = (intl: I18nShape) => {
  const { $t } = intl
  return {
    [EmailStatus.CREATED]: {
      className: 'text-content',
      title: $t({
        defaultMessage: 'Queued',
        id: 'Admin.EmailLog.Status.Queued',
      }),
      description: $t({
        defaultMessage: 'We are preparing the email to be sent',
        id: 'Admin.EmailLog.Status.Description',
      }),
    },
    [EmailStatus.PROCESSED]: {
      className: 'text-content',
      title: $t({
        defaultMessage: 'Sending',
        id: 'Admin.EmailLog.Status.Sending',
      }),
      description: $t({
        defaultMessage: 'We are sending out this email',
        id: 'Admin.EmailLog.Status.Sending.Description',
      }),
    },
    [EmailStatus.DEFERRED]: {
      className: 'text-content-on-attention',
      title: $t({
        defaultMessage: 'Deferred',
        id: 'Admin.EmailLog.Status.Deferred',
      }),
      description: $t({
        defaultMessage:
          'We couldn’t deliver the email immediately, we will retry sending this email a few more times',
        id: 'Admin.EmailLog.Status.Deferred.Description',
      }),
    },
    [EmailStatus.BOUNCED]: {
      className: 'text-action-destructive',
      title: $t({
        defaultMessage: 'Bounced',
        id: 'Admin.EmailLog.Status.Bounced',
      }),
      description: $t({
        defaultMessage:
          'The email was bounced. Most likely the email address is incorrect or no longer exists',
        id: 'Admin.EmailLog.Status.Bounced.Description',
      }),
    },
    [EmailStatus.BLOCKED]: {
      className: 'text-action-destructive',
      title: $t({
        defaultMessage: 'Blocked',
        id: 'Generics.Blocked',
      }),
      description: $t({
        defaultMessage: 'The email was blocked by the receiving email server',
        id: 'Admin.EmailLog.Status.Blocked.Description',
      }),
    },
    [EmailStatus.DROPPED]: {
      className: 'text-action-destructive',
      title: $t({
        defaultMessage: 'Dropped',
        id: 'Admin.EmailLog.Status.Dropped',
      }),
      description: $t({
        defaultMessage: 'We did not send the email',
        id: 'Admin.EmailLog.Status.Dropped.Description',
      }),
    },
    [EmailStatus.SENT]: {
      className: 'text-content-on-positive',
      title: $t({ defaultMessage: 'Sent', id: 'Generics.Sent' }),
      description: $t({
        defaultMessage: 'The email was sent successfully on our end',
        id: 'Admin.EmailLog.Status.Sent.Description',
      }),
    },
    [EmailStatus.DELIVERED]: {
      className: 'text-content-on-positive',
      title: $t({
        defaultMessage: 'Delivered',
        id: 'Generics.Delivered',
      }),
      description: $t({
        defaultMessage: 'The email was delivered successfully on our end',
        id: 'Admin.EmailLog.Status.Delivered.Description',
      }),
    },
  }
}

export const emailTypesFactory = (intl: I18nShape) => {
  const { $t } = intl
  return [
    {
      value: null,
      text: $t({
        defaultMessage: 'All types',
        id: 'Generics.AllTypes',
      }),
    },
    {
      value: EmailType.EmailVerification,
      text: $t({
        defaultMessage: 'Verification',
        id: 'Admin.EmailLog.Type.Verification',
      }),
    },
    {
      value: EmailType.MemberConfirmation,
      text: $t({
        defaultMessage: 'Confirmation',
        id: 'Admin.EmailLog.Type.Confirmation',
      }),
    },
    {
      value: EmailType.MemberInvitation,
      text: $t({
        defaultMessage: 'Invitation',
        id: 'Generics.Invitation',
      }),
    },
    {
      value: EmailType.Notification,
      text: $t({
        defaultMessage: 'Notification',
        id: 'Admin.EmailLog.Type.Notification',
      }),
    },
    {
      value: EmailType.ResetPassword,
      text: $t({
        defaultMessage: 'Reset password',
        id: 'Generics.ResetPassword',
      }),
    },
    {
      value: EmailType.EmailDigest,
      text: $t({
        defaultMessage: 'Email Digest',
        id: 'Generics.EmailDigest',
      }),
    },
    {
      value: EmailType.Unknown,
      text: $t({
        defaultMessage: 'Other',
        id: 'Admin.EmailLog.Type.Other',
      }),
    },
  ]
}

export const emailLogsColumnsFactory = (
  intl: I18nShape,
  emailTypes: ReturnType<typeof emailTypesFactory>,
) => {
  const { $t } = intl
  return [
    {
      Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
      accessor: 'id',
      visible: false,
      width: 'md',
    },
    {
      Header: $t({ defaultMessage: 'Recipient', id: 'Generics.Recipient' }),
      accessor: 'recipient',
      visible: true,
      locked: true,
    },
    {
      Header: $t({ defaultMessage: 'Created at', id: 'Generics.CreatedAt' }),
      accessor: 'createdAt',
      renderer: 'date',
      format: 'datetime',
      width: 'md',
      visible: true,
    },
    {
      Header: $t({ defaultMessage: 'Updated at', id: 'Generics.UpdatedAt' }),
      accessor: 'updatedAt',
      renderer: 'date',
      format: 'datetime',
      width: 'md',
      visible: false,
    },
    {
      Header: $t({ defaultMessage: 'Type', id: 'Generics.Type' }),
      accessor: 'type',
      width: 'md',
      visible: true,
      renderer: (emailLog: EmailLog) => {
        return (
          emailTypes?.find(emailType => emailType.value === emailLog.type)
            ?.text || '-'
        )
      },
    },
    {
      Header: $t({ defaultMessage: 'Status', id: 'Generics.Status' }),
      accessor: 'status',
      visible: true,
      width: 'sm',
      renderer: (emailLog: EmailLog) => (
        <EmailStatusRenderer emailLog={emailLog} />
      ),
    },
    {
      Header: $t({ defaultMessage: 'Reason', id: 'Generics.Reason' }),
      accessor: 'reason',
      visible: false,
      width: 'xl',
      renderer: 'longtext',
    },
  ]
}

export const emailStatusFactory = (
  { $t }: I18nShape,
  emailStatusMapping: ReturnType<typeof emailStatusMappingFactory>,
) => [
  {
    value: null,
    text: $t({
      defaultMessage: 'All statuses',
      id: 'Generics.AllStatuses',
    }),
  },
  ...Object.keys(emailStatusMapping).map(key => ({
    value: key,
    text: emailStatusMapping[key]?.title,
  })),
]
